import { useCallback, useEffect, useRef, useState } from 'react';
import { homePath } from './constants';
import { ReadSelectedOrganizationCache } from './organization-membership-cache';
import { WriteSelectedOrganizationCache } from './organization-membership-cache';
import { buildSignOutTarget } from './sign-out-target';
import { getSwitchFailureMessage, isSessionRefusal, switchOrganization } from './organization-switch';
import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';
import { useLazyOrganizationList } from './use-lazy-organization-list';
import { useOrgSync } from './use-org-sync';
import { notifyUnauthenticated, useSessionBroadcast } from './use-session-broadcast';
import { useSessionStatus } from './use-session-status';
import { useToast } from '../toast/toast-context';

import type { Organization } from './types';
import type { OrganizationSelection } from './types';
import type { ShellSessionState } from './shell-api';

type UseShellOrganizationsOptions = {
  logoutUrl: string;
  userKey: string;
  getOrganizations: () => Promise<Organization[]>;
  getSessionState: () => Promise<ShellSessionState>;
  initialOrganization?: OrganizationSelection | null;
  navigate?: (url: string) => void;
  landInPlace?: (selection: OrganizationSelection) => Promise<void>;
};

type ShellOrganizations = {
  organizations: Organization[];
  organizationsLoading: boolean;
  loadOrganizations: () => void;
  selectedOrganization: OrganizationSelection | null;
  switchingToOrganization: OrganizationSelection | null;
  handleOrganizationChange: (selection: OrganizationSelection) => Promise<void>;
};

type OrganizationSwitching = ShellOrganizations & {
  reactToOrganizationSelection: (selection: OrganizationSelection) => void;
  commitSwitch: (selection: OrganizationSelection) => void;
};

export function useOrganizationSwitching(options: UseShellOrganizationsOptions): OrganizationSwitching {
  const { error } = useToast();
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationSelection | null>(() => {
    if (options.initialOrganization) {
      return options.initialOrganization;
    }
    return ReadSelectedOrganizationCache(options.userKey);
  });
  const [switchingToOrganization, setSwitchingToOrganization] = useState<OrganizationSelection | null>(null);
  const switchingRef = useRef<OrganizationSelection | null>(null);
  const seedRequested = useRef(false);
  const getOrganizations = options.getOrganizations;
  const getSessionState = options.getSessionState;
  const navigate = options.navigate;
  const landInPlace = options.landInPlace;
  const logoutUrl = options.logoutUrl;

  const fetchOrganizations = useCallback(async () => {
    return getOrganizations();
  }, [getOrganizations]);

  const { organizations, organizationsLoading, loadOrganizations } = useLazyOrganizationList({
    userKey: options.userKey,
    fetchOrganizations: fetchOrganizations,
  });

  useEffect(() => {
    if (!seedRequested.current) {
      seedRequested.current = true;
      async function seedSelectedOrganization(): Promise<void> {
        try {
          const state = await getSessionState();
          const selection = OrganizationSelections.From(state);
          if (selection) {
            setSelectedOrganization(selection);
          }
        } catch { } // The next session poll seeds the organization label instead
      }
      void seedSelectedOrganization();
    }
  }, [getSessionState]);

  const userKey = options.userKey;
  useEffect(() => {
    if (selectedOrganization) {
      WriteSelectedOrganizationCache(userKey, selectedOrganization);
    }
  }, [selectedOrganization, userKey]);

  let onResync: ((selection: OrganizationSelection) => void) | undefined;
  if (landInPlace) {
    onResync = (selection: OrganizationSelection) => {
      setSelectedOrganization(selection);
      void landInPlace(selection);
    };
  }

  const { broadcastOrgSwitch, reactToOrganizationSelection } = useOrgSync({
    selectedOrganization: selectedOrganization,
    onSeed: setSelectedOrganization,
    onResync: onResync,
  });

  const commitSwitch = useCallback(
    (selection: OrganizationSelection): void => {
      setSelectedOrganization(selection);
      broadcastOrgSwitch(selection);
    },
    [broadcastOrgSwitch]
  );

  const handleOrganizationChange = useCallback(
    (selection: OrganizationSelection): Promise<void> => {
      async function switchToOrganization(): Promise<void> {
        if (switchingRef.current !== null || OrganizationSelections.IsSame(selectedOrganization, selection)) {
          return;
        }
        switchingRef.current = selection;
        setSwitchingToOrganization(selection);
        try {
          await switchOrganization(selection);
          commitSwitch(selection);
          if (landInPlace) {
            await landInPlace(selection);
            switchingRef.current = null;
            setSwitchingToOrganization(null);
          } else if (navigate) {
            navigate(homePath);
          } else {
            window.location.assign(homePath);
          }
        } catch (caught) {
          switchingRef.current = null;
          setSwitchingToOrganization(null);
          if (isSessionRefusal(caught)) {
            notifyUnauthenticated(logoutUrl);
          } else {
            error(getSwitchFailureMessage(caught));
          }
        }
      }
      return switchToOrganization();
    },
    [error, commitSwitch, selectedOrganization, navigate, landInPlace, logoutUrl]
  );

  return {
    organizations: organizations,
    organizationsLoading: organizationsLoading,
    loadOrganizations: loadOrganizations,
    selectedOrganization: selectedOrganization,
    switchingToOrganization: switchingToOrganization,
    handleOrganizationChange: handleOrganizationChange,
    reactToOrganizationSelection: reactToOrganizationSelection,
    commitSwitch: commitSwitch,
  };
}

export function useShellOrganizations(options: UseShellOrganizationsOptions): ShellOrganizations {
  const switching = useOrganizationSwitching(options);
  const logoutUrl = options.logoutUrl;

  useSessionBroadcast({
    onLogout: () => {
      window.location.assign(buildSignOutTarget(logoutUrl));
    },
  });

  useSessionStatus({ redirectUrl: logoutUrl, onOrgChange: switching.reactToOrganizationSelection });

  return switching;
}
